/**
 * Capacitor native platform utilities for OAuth authentication.
 *
 * In Capacitor apps (iOS/Android), OAuth redirects can't rely on WKWebView's
 * cookie synchronization for PKCE. Instead, we:
 * 1. Open OAuth URLs in an in-app browser (SFSafariViewController / Chrome Custom Tabs)
 * 2. Capture the callback via an environment-specific custom URL scheme
 * 3. Exchange the auth code client-side where the PKCE verifier is accessible
 */
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import {
  getNativeAuthCallbackUrlForEnvironment,
  getNativePlatformCallbackUrlForEnvironment,
  isKnownNativeAuthCallbackUrl,
  isKnownNativePlatformCallbackUrl,
  resolveNativeAppEnvironment,
} from "@/lib/capacitor/native-callbacks";
import { createErrorReporter } from "@/lib/observability/report-error";

const report = createErrorReporter("lib.capacitor.native-auth", "auth");

/**
 * Check if running inside a Capacitor native app (iOS/Android).
 * Returns false during SSR or in a regular browser.
 */
export function isNativePlatform(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const native = Capacitor.isNativePlatform();
    if (native) {
      console.log(
        `[native-auth] Native platform detected: ${Capacitor.getPlatform()}`
      );
    }
    return native;
  } catch (err) {
    // Expected on non-Capacitor environments where the bridge isn't available
    console.error("[native-auth] Capacitor detection failed:", err);
    report(err, { action: "isNativePlatform", severity: "warning" });
    return false;
  }
}

/**
 * Get the redirect URL for Supabase auth callbacks on native.
 * Uses an environment-specific custom scheme so stage and prod apps can
 * coexist on the same device without stealing each other's callbacks.
 */
export function getNativeAuthCallbackUrl(): string {
  const environment =
    typeof window !== "undefined"
      ? resolveNativeAppEnvironment(window.location.hostname)
      : "prod";

  return getNativeAuthCallbackUrlForEnvironment(environment);
}

/**
 * Get the redirect URL for platform OAuth callbacks on native.
 */
export function getNativePlatformCallbackUrl(): string {
  const environment =
    typeof window !== "undefined"
      ? resolveNativeAppEnvironment(window.location.hostname)
      : "prod";

  return getNativePlatformCallbackUrlForEnvironment(environment);
}

/**
 * Open a URL in the system's in-app browser.
 * iOS: SFSafariViewController, Android: Chrome Custom Tabs.
 */
export async function openInAppBrowser(url: string): Promise<void> {
  console.log("[native-auth] Opening in-app browser:", url.slice(0, 120));
  await Browser.open({ url });
  console.log("[native-auth] In-app browser opened successfully");
}

/**
 * Close the in-app browser (e.g., after OAuth callback is received).
 */
export async function closeInAppBrowser(): Promise<void> {
  try {
    await Browser.close();
  } catch (err) {
    // Browser may already be closed — not an error condition
    console.error(
      "[native-auth] Browser.close() failed (may already be closed):",
      err
    );
    report(err, { action: "closeInAppBrowser", severity: "warning" });
  }
}

/**
 * Wait for the in-app browser to be dismissed by the user.
 * Resolves when SFSafariViewController (iOS) / Chrome Custom Tabs (Android)
 * is closed — either by the user tapping "Done" or programmatically via
 * Browser.close(). Includes a 5-minute safety timeout to prevent infinite hangs
 * in case the browserFinished event is never fired.
 */
export function waitForBrowserDismissed(): Promise<void> {
  return new Promise((resolve) => {
    const TIMEOUT_MS = 5 * 60 * 1000;
    const timer = setTimeout(() => {
      console.warn(
        "[native-auth] Browser dismissal timeout after 5 minutes — resolving"
      );
      resolve();
    }, TIMEOUT_MS);

    Browser.addListener("browserFinished", () => {
      clearTimeout(timer);
      console.log("[native-auth] Browser dismissed by user");
      resolve();
    });
  });
}

/**
 * Extract the authorization code from a callback URL.
 * Handles both standard URLs and custom scheme URLs.
 */
export function extractCodeFromUrl(url: string): string | null {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get("code");
  } catch (err) {
    // Fallback regex for custom scheme URLs that URL constructor can't parse
    console.error(
      "[native-auth] URL parsing failed, using regex fallback:",
      err
    );
    report(err, { action: "extractCodeFromUrl" });
    const match = url.match(/[?&]code=([^&#]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }
}

/**
 * Extract query parameters from a callback URL.
 */
export function extractParamsFromUrl(
  url: string
): Record<string, string> | null {
  try {
    const urlObj = new URL(url);
    const params: Record<string, string> = {};
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return Object.keys(params).length > 0 ? params : null;
  } catch (err) {
    console.error("[native-auth] Failed to extract params from URL:", err);
    report(err, { action: "extractParamsFromUrl" });
    return null;
  }
}

/**
 * Check if a URL is a native auth callback for any supported app environment.
 */
export function isAuthCallbackUrl(url: string): boolean {
  return isKnownNativeAuthCallbackUrl(url);
}

/**
 * Check if a URL is a native platform OAuth callback for any supported app environment.
 */
export function isPlatformCallbackUrl(url: string): boolean {
  return isKnownNativePlatformCallbackUrl(url);
}
